/** The bytes of the photos that wait on the phone, and the limits for them (design 10.8). */

import { keys, read, remove, type Scope, write } from "./db";

export const MAX_WAITING_PHOTOS = 200;
export const MAX_WAITING_BYTES = 100_000_000;
const PERSIST_KEY = "persist-asked";
const LOST_AFTER_MS = 3_600_000;

interface Bytes {
  type: string;
  bytes: ArrayBuffer;
}

export const photoKey = (scope: Scope, id: string) => scope.key(`photo:${id}`);

/** Why the phone cannot keep one more photo of `size` bytes, or null. */
export function capProblem(held: readonly { size: number }[], size: number): string | null {
  const total = held.reduce((sum, photo) => sum + photo.size, 0);
  if (held.length < MAX_WAITING_PHOTOS && total + size <= MAX_WAITING_BYTES) return null;
  return `The phone is full: ${held.length} photos (${Math.round(total / 1_000_000)} MB) wait to send. Get a signal and send them on the Sync page, then take this photo.`;
}

/** True only when the bytes are in IndexedDB. A private window can refuse them. */
export async function keepBytes(scope: Scope, id: string, blob: Blob): Promise<boolean> {
  await write<Bytes>(photoKey(scope, id), { type: blob.type, bytes: await blob.arrayBuffer() });
  return (await read<Bytes>(photoKey(scope, id))) !== null;
}

export async function photoBlob(scope: Scope, id: string): Promise<Blob | null> {
  const kept = await read<Bytes>(photoKey(scope, id));
  return kept ? new Blob([kept.value.bytes], { type: kept.value.type }) : null;
}

export const dropBytes = (scope: Scope, ids: string[]) => remove(ids.map((id) => photoKey(scope, id)));

/** Remove the bytes that no entry holds. Wait one hour, so that the bytes of a new photo are safe. */
export async function dropLostBytes(scope: Scope, held: ReadonlySet<string>, now: number): Promise<void> {
  const prefix = photoKey(scope, "");
  const lost = (await keys(prefix)).filter((key) => !held.has(key.slice(prefix.length)));
  const kept = await Promise.all(lost.map(async (key) => ({ key, at: (await read(key))?.at ?? now })));
  await remove(kept.filter((found) => now - found.at > LOST_AFTER_MS).map((found) => found.key));
}

/** Ask one time for storage that the browser does not clear when space is low. */
export async function askPersist(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist || (await read<boolean>(PERSIST_KEY))) return;
  await write(PERSIST_KEY, true);
  await navigator.storage.persist().catch(() => false);
}
